import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { DefaultSearchType } from 'algolia-api/types/DefaultSearchType';
import { ArticleSearchHero } from 'components/ArticleSearchHero/ArticleSearchHero';
import { BannerAds } from 'components/BannerAds';
import { Box } from 'components/Box';
import { PageFooter } from 'components/PageFooter/PageFooter';
import { PrintHidden } from 'components/PrintHidden/PrintHidden';
import { SearchHero } from 'components/Search/SearchHero';
import { ClientOnly } from 'containers/ClientOnly';
import { NoAmp } from 'containers/NoAmp';
import { PageHeaderContainer } from 'containers/PageHeaderContainer';
import { useElementReachedEffect } from 'hooks/viewport/useElementReachedEffect';
import { BaseLayout } from 'layouts/Base/BaseLayout';
import { CmsApiHeroSearch } from 'modules/cms/api/types/CmsApiHeroSearch';
import {
  CmsApiPage,
  CmsApiTableOfContentsParent,
} from 'modules/cms/api/types/CmsApiPage';
import { CmsMobileTableOfContents } from 'modules/cms/components/TableOfContents/MobileTableOfContents';
import { setWithSearchHero } from 'modules/main/store/ducks/layout.actions';
import { useCurrentUserIsStaff } from 'modules/main/store/hooks/useCurrentUserIsStaff';
import { useMainDispatch } from 'modules/main/store/hooks/useMainDispatch';
import { useMainSelector } from 'modules/main/store/hooks/useMainSelector';
import { StaffPageHeader } from 'modules/staff/components/PageHeader/StaffPageHeader';
import { colors } from 'theme/theme';
import { useAmpContext } from 'utils/AmpContext';
import { trackEvent } from 'utils/analytics/track/trackEvent';
import { deleteCookie, getListCookie } from 'utils/cookies';
import { isDefined } from 'utils/functional';

import { LayoutChildrenHolder } from './Layout.styled';
import { LayoutFixedStaffTools } from './LayoutFixedStaffTools';

type Props = {
  noAds?: boolean;
  buttonBar?: ReactNode;
  urlByLocale?: Record<UserLocale, string>;
  navSection?: 'MAIN' | 'STAFF';
  defaultWithSearchHero?: boolean;
  children?: ReactNode;
  searchMode?: boolean;
  onSearch?: () => void;
  darkMode?: boolean;
  showListingCounts?: boolean;
  hideFooterDivider?: boolean;
  stickySidebar?: ReactNode;
  bottomTableOfContents?: CmsApiTableOfContentsParent[];
  toolbar?: ReactNode;
  hero?: CmsApiHeroSearch | null | undefined;
  content?: CmsApiPage;
  defaultSearchType?: DefaultSearchType;
  preFooter?: ReactNode;
};

export function Layout({
  noAds,
  buttonBar,
  content,
  urlByLocale,
  navSection = 'MAIN',
  defaultWithSearchHero,
  children,
  onSearch,
  darkMode,
  stickySidebar,
  bottomTableOfContents,
  showListingCounts,
  hideFooterDivider,
  toolbar,
  searchMode,
  hero,
  defaultSearchType,
  preFooter,
}: Props) {
  const isStaff = useCurrentUserIsStaff();

  const withSearchHero = useMainSelector(
    (state) => state.layout.withSearchHero,
  );

  const dispatch = useMainDispatch();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showStickyMenu, setShowStickyMenu] = useState(false);

  const heroRef = useRef(null);
  const queuedTrackers = getListCookie('trackEvents');
  const requestIsAmp = useAmpContext();

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    queuedTrackers.forEach((e: any) => trackEvent(e.type));
    deleteCookie('trackEvents');
  }, [queuedTrackers]);

  useEffect(() => {
    if (
      isDefined(defaultWithSearchHero) &&
      defaultWithSearchHero !== withSearchHero
    ) {
      dispatch(setWithSearchHero(defaultWithSearchHero));
    }
  }, [defaultWithSearchHero, withSearchHero, dispatch]);

  const showSearchHero = isDefined(defaultWithSearchHero)
    ? defaultWithSearchHero
    : withSearchHero;

  const hideHeroSearch = isDefined(searchMode) && searchMode && !requestIsAmp;

  const bottomOfPageRef = useRef<HTMLDivElement>(null);
  useElementReachedEffect({
    ref: bottomOfPageRef,
    onReach: useCallback(() => {
      // Prevent event from triggering when going from one page to another
      // and the contents of the page is loading
      if (document.documentElement.scrollTop === 0) return false;

      trackEvent('Scroll: Bottom of Page', {
        reached_through: 'Scrolling',
      });

      return true;
    }, []),
  });

  return (
    <BaseLayout
      buttonBar={buttonBar}
      stickyHeaderHeight={[
        60,
        60,
        60,
        searchMode || !showSearchHero ? 83 : null,
      ]}
      header={
        <>
          {!noAds && <BannerAds />}

          {navSection === 'STAFF' ? (
            <StaffPageHeader />
          ) : (
            <PageHeaderContainer
              showDesktopSearch={searchMode || !showSearchHero}
              searchMode={Boolean(searchMode)}
              defaultSearchType={defaultSearchType}
            />
          )}
        </>
      }
    >
      {toolbar}

      {isStaff && content && <LayoutFixedStaffTools content={content} />}

      {hero && (
        <Box
          position="relative"
          height={hideHeroSearch ? 0 : 'auto'}
          overflow={hideHeroSearch ? 'hidden' : undefined}
          bg={darkMode ? colors.backgroundGrey : undefined}
        >
          <div ref={heroRef}>
            {defaultSearchType === 'CMS_PAGE' ? (
              <ArticleSearchHero
                title={hero.text}
                image={hero.image}
                imageMobile={hero.imageMobile}
                // @ts-expect-error TS(2322): Type 'CmsApiPage | undefined' is not assignable to... Remove this comment to see the full error message
                content={content}
              />
            ) : (
              <SearchHero
                setShowStickyMenu={setShowStickyMenu}
                title={hero.text}
                // @ts-expect-error TS(2322): Type 'FilestackImageData | null | undefined' is no... Remove this comment to see the full error message
                image={hero.image}
                // @ts-expect-error TS(2322): Type 'FilestackImageData | null | undefined' is no... Remove this comment to see the full error message
                imageMobile={hero.imageMobile}
                searchMode={hideHeroSearch}
                navSection={navSection}
                // @ts-expect-error TS(2322): Type '((...args: any[]) => any) | undefined' is no... Remove this comment to see the full error message
                onSearch={onSearch}
                defaultSearchType={defaultSearchType}
                showListingCounts={showListingCounts}
              />
            )}
          </div>
        </Box>
      )}

      <LayoutChildrenHolder
        $darkMode={darkMode}
        $hasPreFooter={Boolean(preFooter)}
      >
        {stickySidebar ? <NoAmp>{stickySidebar}</NoAmp> : null}
        {bottomTableOfContents && bottomTableOfContents.length > 0 && (
          <NoAmp>
            <ClientOnly>
              <CmsMobileTableOfContents
                parents={bottomTableOfContents}
                hasButtonBar={Boolean(buttonBar)}
              />
            </ClientOnly>
          </NoAmp>
        )}
        {children}
      </LayoutChildrenHolder>

      {preFooter}

      <PrintHidden>
        <div ref={bottomOfPageRef} />
        <PageFooter urlByLocale={urlByLocale} hideDivider={hideFooterDivider} />
      </PrintHidden>
    </BaseLayout>
  );
}
